import { FileText, ZoomIn, ZoomOut } from 'lucide-react'
import * as React from 'react'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactMetadataBlock } from '@/components/project/artifact-viewer/ArtifactMetadataBlock'
import { ExtractedImageWarningBanner } from '@/components/project/artifact-viewer/ExtractedImageWarningBanner'
import { getExtractedImagePreviewIssues } from '@/components/project/artifact-viewer/utils/extractedImagePreviewWarnings'
import { formatFileSize } from '@/components/project/artifact-viewer/utils/format'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useArtifactImageUrl } from '@/hooks/artifact-viewer'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

const ZOOM_STEP = 0.25
const MIN_ZOOM = 0.25
const MAX_ZOOM = 4
const WHEEL_ZOOM_FACTOR = 0.002
/** Matches `p-2` horizontal + vertical inset on the scroll container (8px × 2 per axis). */
const PREVIEW_INNER_PADDING_PX = 16
/** Skip fit until the container has a measurable layout (avoids div-by-zero / bogus rects). */
const MIN_CONTAINER_FOR_FIT_PX = 32

export interface ImageViewerProps {
	artifact: ArtifactResponse
	projectId: string
	artifactId: string
	activeTab: 'preview' | 'metadata'
	onDownload: () => Promise<void>
	downloading: boolean
}

export function ImageViewer({
	artifact,
	projectId,
	artifactId,
	activeTab,
	onDownload,
	downloading,
}: ImageViewerProps) {
	const { t } = useTranslation()
	const zoomHintId = React.useId()
	const {
		url: imageUrl,
		loading: urlLoading,
		error: urlError,
	} = useArtifactImageUrl(projectId, artifactId, artifact.status)
	const containerRef = React.useRef<HTMLDivElement>(null)
	const transformWrapperRef = React.useRef<HTMLDivElement>(null)
	const [zoom, setZoom] = React.useState(1)
	const [translate, setTranslate] = React.useState({ x: 0, y: 0 })
	const [imageError, setImageError] = React.useState(false)
	const [dimensions, setDimensions] = React.useState<{ width: number; height: number } | null>(null)
	const [extractedImageWarningDismissed, setExtractedImageWarningDismissed] = React.useState(false)
	const zoomRef = React.useRef(zoom)
	const translateRef = React.useRef(translate)
	zoomRef.current = zoom
	translateRef.current = translate

	const panStartRef = React.useRef<{
		clientX: number
		clientY: number
		translateX: number
		translateY: number
	} | null>(null)
	const hasInitializedRef = React.useRef(false)

	const extractedImageIssues = React.useMemo(() => {
		if (artifact.source !== 'output' || artifact.type !== 'image' || !dimensions) {
			return []
		}
		return getExtractedImagePreviewIssues(dimensions.width, dimensions.height, artifact.file_size)
	}, [artifact.file_size, artifact.source, artifact.type, dimensions])

	const showExtractedImageWarning =
		extractedImageIssues.length > 0 && !extractedImageWarningDismissed

	/** Fit large images to the preview pane; 100% means native size only when it already fits. */
	React.useLayoutEffect(() => {
		if (!dimensions || hasInitializedRef.current) return
		const el = containerRef.current
		if (!el) return

		let resizeObserver: ResizeObserver | null = null
		let rafId = 0

		const runFit = () => {
			if (hasInitializedRef.current || !containerRef.current || !dimensions) return
			const c = containerRef.current
			const rect = c.getBoundingClientRect()
			if (rect.width < MIN_CONTAINER_FOR_FIT_PX || rect.height < MIN_CONTAINER_FOR_FIT_PX) return
			const availW = Math.max(1, rect.width - PREVIEW_INNER_PADDING_PX)
			const availH = Math.max(1, rect.height - PREVIEW_INNER_PADDING_PX)
			const fitZoom = Math.min(1, availW / dimensions.width, availH / dimensions.height)
			const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom))
			const tx = (rect.width - dimensions.width * z) / 2
			const ty = (rect.height - dimensions.height * z) / 2
			// Refs mirror state so wheel/pan handlers read consistent values before React commits.
			zoomRef.current = z
			translateRef.current = { x: tx, y: ty }
			setZoom(z)
			setTranslate({ x: tx, y: ty })
			hasInitializedRef.current = true
			resizeObserver?.disconnect()
			resizeObserver = null
		}

		runFit()
		if (!hasInitializedRef.current) {
			rafId = requestAnimationFrame(runFit)
		}
		if (!hasInitializedRef.current) {
			resizeObserver = new ResizeObserver(() => {
				runFit()
			})
			resizeObserver.observe(el)
		}

		return () => {
			if (rafId) cancelAnimationFrame(rafId)
			resizeObserver?.disconnect()
		}
	}, [dimensions])

	React.useEffect(() => {
		const el = containerRef.current
		if (!el) return
		const onWheel = (e: WheelEvent) => {
			if (!e.ctrlKey) return
			const dims = dimensions
			if (!dims) return
			e.preventDefault()
			const rect = el.getBoundingClientRect()
			const cx = e.clientX - rect.left
			const cy = e.clientY - rect.top
			const z = zoomRef.current
			const tx = translateRef.current.x
			const ty = translateRef.current.y
			const zoomNext = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * (1 - e.deltaY * WHEEL_ZOOM_FACTOR)))
			const px = (cx - tx) / z
			const py = (cy - ty) / z
			setTranslate({ x: cx - px * zoomNext, y: cy - py * zoomNext })
			setZoom(zoomNext)
		}
		el.addEventListener('wheel', onWheel, { passive: false })
		return () => el.removeEventListener('wheel', onWheel)
	}, [dimensions])

	React.useEffect(() => {
		const el = containerRef.current
		if (!el || !dimensions) return
		const onPointerDown = (e: PointerEvent) => {
			if (e.button !== 0) return
			panStartRef.current = {
				clientX: e.clientX,
				clientY: e.clientY,
				translateX: translateRef.current.x,
				translateY: translateRef.current.y,
			}
			el.classList.remove('cursor-grab')
			el.classList.add('cursor-grabbing')
			transformWrapperRef.current?.classList.remove('transition-[transform]', 'duration-150')
			el.setPointerCapture(e.pointerId)
		}
		const onPointerMove = (e: PointerEvent) => {
			const start = panStartRef.current
			if (!start) return
			const tx = start.translateX + (e.clientX - start.clientX)
			const ty = start.translateY + (e.clientY - start.clientY)
			translateRef.current = { x: tx, y: ty }
			const wrapper = transformWrapperRef.current
			if (wrapper) {
				const z = zoomRef.current
				wrapper.style.transform = `translate(${tx}px, ${ty}px) scale(${z})`
				wrapper.style.transformOrigin = '0 0'
			}
		}
		const onPointerUp = (e: PointerEvent) => {
			if (e.button !== 0) return
			panStartRef.current = null
			el.classList.remove('cursor-grabbing')
			el.classList.add('cursor-grab')
			transformWrapperRef.current?.classList.add('transition-[transform]', 'duration-150')
			setTranslate(translateRef.current)
			el.releasePointerCapture(e.pointerId)
		}
		const onPointerCancel = (e: PointerEvent) => {
			panStartRef.current = null
			el.classList.remove('cursor-grabbing')
			el.classList.add('cursor-grab')
			transformWrapperRef.current?.classList.add('transition-[transform]', 'duration-150')
			el.releasePointerCapture(e.pointerId)
		}
		el.addEventListener('pointerdown', onPointerDown)
		el.addEventListener('pointermove', onPointerMove)
		el.addEventListener('pointerup', onPointerUp)
		el.addEventListener('pointercancel', onPointerCancel)
		return () => {
			el.removeEventListener('pointerdown', onPointerDown)
			el.removeEventListener('pointermove', onPointerMove)
			el.removeEventListener('pointerup', onPointerUp)
			el.removeEventListener('pointercancel', onPointerCancel)
		}
	}, [dimensions])

	const handleZoomIn = React.useCallback(() => {
		const el = containerRef.current
		if (!el || !dimensions) {
			setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
			return
		}
		const rect = el.getBoundingClientRect()
		const cx = rect.width / 2
		const cy = rect.height / 2
		const newZoom = Math.min(MAX_ZOOM, zoom + ZOOM_STEP)
		const px = (cx - translate.x) / zoom
		const py = (cy - translate.y) / zoom
		setTranslate({ x: cx - px * newZoom, y: cy - py * newZoom })
		setZoom(newZoom)
	}, [dimensions, zoom, translate.x, translate.y])

	const handleZoomOut = React.useCallback(() => {
		const el = containerRef.current
		if (!el || !dimensions) {
			setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
			return
		}
		const rect = el.getBoundingClientRect()
		const cx = rect.width / 2
		const cy = rect.height / 2
		const newZoom = Math.max(MIN_ZOOM, zoom - ZOOM_STEP)
		const px = (cx - translate.x) / zoom
		const py = (cy - translate.y) / zoom
		setTranslate({ x: cx - px * newZoom, y: cy - py * newZoom })
		setZoom(newZoom)
	}, [dimensions, zoom, translate.x, translate.y])

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset pan/zoom and warnings when the preview target or presigned URL changes
	React.useEffect(() => {
		setImageError(false)
		setZoom(1)
		setTranslate({ x: 0, y: 0 })
		setDimensions(null)
		hasInitializedRef.current = false
		setExtractedImageWarningDismissed(false)
	}, [artifactId, imageUrl])

	const handleImageLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget
		setDimensions({ width: img.naturalWidth, height: img.naturalHeight })
	}, [])

	const metadataContent = (
		<div className="flex-1 min-h-0 overflow-auto space-y-6 p-4">
			<ArtifactMetadataBlock artifact={artifact} onDownload={onDownload} downloading={downloading} />
		</div>
	)

	const previewPending = (
		<div className="flex flex-col items-center justify-center py-12 text-center p-4 min-h-[12rem]">
			<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
			<p className="mt-3 text-sm font-medium text-foreground">
				{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
			</p>
			<p className="mt-1 text-sm text-muted-foreground">
				{t('pages.projectDetail.artifactPreview.previewPending')}
			</p>
		</div>
	)

	const previewLoading = (
		<div className="flex flex-col items-center justify-center py-12 gap-2 text-sm text-muted-foreground min-h-[12rem]">
			<Spinner className="size-4" aria-hidden />
			{t('common.loading')}
		</div>
	)

	const previewUrlError = (
		<div className="flex flex-col items-center justify-center py-12 text-center p-4 min-h-[12rem]">
			<p className="text-sm text-muted-foreground">
				{t('pages.projectDetail.artifactPreview.errorLoading')}
			</p>
		</div>
	)

	return (
		<div className="relative flex flex-col h-full min-h-0">
			{activeTab === 'metadata' && metadataContent}

			{activeTab === 'preview' && (
				<div className="flex-1 min-h-0 flex flex-col">
					{artifact.status !== 'completed' && previewPending}

					{artifact.status === 'completed' && urlLoading && previewLoading}

					{artifact.status === 'completed' &&
						!urlLoading &&
						(urlError || !imageUrl || imageError) &&
						previewUrlError}

					{artifact.status === 'completed' && !urlLoading && !urlError && imageUrl && !imageError && (
						<div className="flex-1 min-h-0 flex flex-col p-2 gap-2">
							{showExtractedImageWarning && dimensions ? (
								<ExtractedImageWarningBanner
									issues={extractedImageIssues}
									width={dimensions.width}
									height={dimensions.height}
									formattedFileSize={formatFileSize(artifact.file_size)}
									onDismiss={() => setExtractedImageWarningDismissed(true)}
								/>
							) : null}
							<span id={zoomHintId} className="sr-only">
								{t('pages.projectDetail.artifactPreview.zoomHint')}
							</span>
							<div
								ref={containerRef}
								className={cn(
									'flex-1 min-h-0 overflow-hidden rounded-lg border bg-muted/30 p-2',
									dimensions && 'cursor-grab'
								)}
							>
								<div
									ref={transformWrapperRef}
									className="min-w-0 min-h-0 inline-block will-change-transform transition-[transform] duration-150"
									style={
										dimensions
											? {
													width: dimensions.width,
													height: dimensions.height,
													transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom})`,
													transformOrigin: '0 0',
												}
											: undefined
									}
								>
									<img
										src={imageUrl}
										alt={artifact.name}
										aria-describedby={zoomHintId}
										className={cn(
											'block',
											dimensions ? 'w-full h-full object-contain' : 'max-w-full max-h-full'
										)}
										onLoad={handleImageLoad}
										onError={() => setImageError(true)}
										draggable={false}
									/>
								</div>
							</div>

							<div className="flex items-center justify-center gap-1">
								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={handleZoomOut}
									disabled={zoom <= MIN_ZOOM}
									aria-label={t('pages.projectDetail.artifactPreview.zoomOut')}
									className="size-8"
								>
									<ZoomOut className="size-4" aria-hidden />
								</Button>

								<span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
									{Math.round(zoom * 100)}%
								</span>

								<Button
									type="button"
									variant="outline"
									size="icon"
									onClick={handleZoomIn}
									disabled={zoom >= MAX_ZOOM}
									aria-label={t('pages.projectDetail.artifactPreview.zoomIn')}
									className="size-8"
								>
									<ZoomIn className="size-4" aria-hidden />
								</Button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	)
}
